import { Form, FormHandlers, FormProps } from '@doveit/bricks';
import React from 'react';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import agencyRegistryValidationSchema from './AgencyRegistryForm.schema';
import { BooleanString } from '../../../utils/boolean-string/booleanString';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';
import { GeoFormModel } from '../../../geo/components/form-geo-field/types';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import { deepMerge } from '../../../utils/object/object';

export interface AgencyRegistryFormModel {
  name: string,
  shortName: string,
  phoneNumber: string,
  openingHours: string,
  startDate: string,
  address: GeoFormModel,
  isLuxury: BooleanString,
  isFranchising: BooleanString,
}

export interface AgencyRegistryFormProps {
  initialValues?: Partial<AgencyRegistryFormModel>,
  loading?: FormProps<AgencyRegistryFormModel>['loading'],
  disabled?: FormProps<AgencyRegistryFormModel>['disabled'],
  innerRef?: React.MutableRefObject<FormHandlers>,
  onSubmit: (values: AgencyRegistryFormModel) => void,
  hideStartDate?: boolean,
}

export const AGENCY_REGISTRY_FORM_DEFAULT_VALUES: AgencyRegistryFormModel = {
  name: '',
  shortName: '',
  phoneNumber: '',
  openingHours: '',
  startDate: '',
  address: GEO_FORM_MODEL_DEFAULT_VALUES,
  isLuxury: 'false',
  isFranchising: 'false',
};

const AgencyRegistryForm: React.FC<AgencyRegistryFormProps> = ({
  initialValues = {},
  innerRef,
  hideStartDate = false,
  ...rest
}) => {
  const toggleManualEntry = React.useMemo(
    () => !initialValues.address?.latitude
      && !initialValues.address?.longitude
      && !!initialValues.address?.normalizedAddress?.description,
    [initialValues.address?.latitude, initialValues.address?.longitude, initialValues.address?.normalizedAddress],
  );

  const mergedValues: AgencyRegistryFormModel = React.useMemo(() => deepMerge(
    AGENCY_REGISTRY_FORM_DEFAULT_VALUES,
    initialValues,
  ), [initialValues]);

  return (
    <Form
      initialValues={mergedValues}
      validationSchema={agencyRegistryValidationSchema}
      enableReinitialize
      innerRef={innerRef}
      {...rest}
    >
      <Form.Item size={{ MD: 1 / 2 }}>
        <Form.Input
          name="name"
          label="Nome"
          aria-label="Campo per inserire il nome dell'agenzia"
          placeholder={INPUT_PLACEHOLDERS.AGENCY_NAME}
          required
        />
      </Form.Item>

      <Form.Item size={{ MD: 1 / 2 }}>
        <Form.Input
          name="shortName"
          label="Nome abbreviato"
          aria-label="Campo per inserire il nome abbreviato dell'agenzia"
          placeholder={INPUT_PLACEHOLDERS.AGENCY_SHORT_NAME}
          required
        />
      </Form.Item>

      <Form.Item size={{ MD: 1 / 2 }}>
        <Form.Input
          name="phoneNumber"
          label="Telefono"
          aria-label="Campo per inserire il telefono dell'agenzia"
          placeholder={INPUT_PLACEHOLDERS.CONTACT_PHONE_NUMBER}
        />
      </Form.Item>

      {!hideStartDate && (
        <Form.Item size={{ MD: 1 / 2 }}>
          <Form.Input
            name="startDate"
            type="date"
            label="Data di inizio"
            aria-label="Campo per inserire la data di inizio attività dell'agenzia"
            required
          />
        </Form.Item>
      )}

      <Form.Item>
        <FormGeoField
          name="address"
          enableManualEntry
          toggleManualEntry={toggleManualEntry}
        />
      </Form.Item>

      <Form.Item>
        <Form.TextArea
          name="openingHours"
          label="Orari di apertura"
          aria-label="Campo per inserire gli orari di apertura dell'agenzia"
          placeholder={INPUT_PLACEHOLDERS.AGENCY_OPENING_HOURS}
          rows={5}
        />
      </Form.Item>

      <Form.Item size={{ MD: 1 / 2, LG: 1 / 4 }}>
        <Form.YesOrNotButtons
          name="isLuxury"
          label="È luxury?"
        />
      </Form.Item>

      <Form.Item size={{ MD: 1 / 2, LG: 1 / 4 }}>
        <Form.YesOrNotButtons
          name="isFranchising"
          label="È un franchising?"
        />
      </Form.Item>
    </Form>
  );
};

export default AgencyRegistryForm;
